import { type DeliveryTimeSlot, DeliveryTimeSlotMap, Platform } from '@constants'
import { useApiBlockoutDeliveryDays } from '@hooks-api/fortune/use-api-blockout-delivery-days'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { Button } from '@pets-ui/Button/Button'
import { HStack } from '@pets-ui/HStack/HStack'
import { Clock } from '@pets-ui/icons'
import { Input } from '@pets-ui/Input/Input'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { getDeliveryDateRange } from '@utils/get-delivery-date-range'
import { format, fromUnixTime } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { CardSection } from '../../../../../components/CardSection/CardSection'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'
import { usePreOrderDeliveryDate } from '../../../../fp-b2b-shared/hooks/use-pre-order-delivery-date'
import { DialogDeliveryTimeSlot } from '../OrderNotes/DialogDeliveryTimeSlot'

const styles = stylex.create({
  deliveryTime: {
    borderColor: colors.grey300,
    justifyContent: 'flex-start',
    paddingRight: 36,
  },
  endIcon: {
    position: 'absolute',
    right: 16,
  },
  disabled: {
    backgroundColor: colors.grey200,
  },
  input: {
    fontSize: 14,
  },
})

export function DeliveryNotes() {
  const { campaignCode, countryCode, projectCode, isInRetryPaymentPage, campaignId, platform } = useCheckout()

  const { destructuredData: userInfo } = useApiCampaignMember({ code: campaignCode })
  const isShopManager = userInfo?.roles.includes('shop_manager')

  const preOrderDeliveryDate = usePreOrderDeliveryDate({ campaignId, projectCode, countryCode, campaignCode, platform })

  const { destructuredData: blockoutDeliveryDays } = useApiBlockoutDeliveryDays({
    country_code: countryCode,
    project_code: projectCode,
    platform: Platform.FASTPASS,
    pre_order_delivery_date: preOrderDeliveryDate,
  })

  const { control } = useFormContext<CheckoutOrder>()
  const [isOpen, setOpen] = useState(false)
  const formDeliveryDate = useWatch({ control, name: 'delivery_date' })
  const formDeliveryTimeSlot = useWatch({ control, name: 'delivery_time_slot' })

  const excludeDays = useMemo(() => {
    const weekdays: number[] = []
    if (blockoutDeliveryDays?.is_sat_on === false && !weekdays.includes(6)) weekdays.push(6)
    if (blockoutDeliveryDays?.is_sun_on === false && !weekdays.includes(0)) weekdays.push(0)
    return weekdays
  }, [blockoutDeliveryDays?.is_sat_on, blockoutDeliveryDays?.is_sun_on])

  const { minDate, maxDate, filterDate } = useMemo(() => {
    const latestDeliveryDate = fromUnixTime(blockoutDeliveryDays?.latest_delivery_date ?? 0)
    return getDeliveryDateRange({
      startDate: latestDeliveryDate,
      maxSelectableDeliveryDays: blockoutDeliveryDays?.max_selectable_delivery_days ?? 365,
      blockoutDeliveryDays: blockoutDeliveryDays?.blockout_delivery_days,
      excludeDays,
    })
  }, [blockoutDeliveryDays?.latest_delivery_date, blockoutDeliveryDays?.max_selectable_delivery_days, blockoutDeliveryDays?.blockout_delivery_days, excludeDays])

  const orderNotesDescription = useMemo(() => {
    if (isShopManager) {
      return '實際配送，依物流安排為主'
    }
  }, [isShopManager])

  const deliveryTimeSlotRef = useRef<DeliveryTimeSlot>(formDeliveryTimeSlot)

  useEffect(() => {
    if (deliveryTimeSlotRef.current) return
    deliveryTimeSlotRef.current = formDeliveryTimeSlot
  }, [formDeliveryTimeSlot])

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <>
      <CardSection title="收貨備註" description={orderNotesDescription}>
        <HStack gap={3} alignItems="center" width="100%">
          {isShopManager
          && (
            <VStack gap={2} flex={1}>
              <Text variant="body">預計到貨日</Text>
              <Controller
                control={control}
                name="delivery_date"
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhTW}>
                    <DatePicker
                      {...field}
                      value={new Date(formDeliveryDate)}
                      format="yyyy-MM-dd"
                      sx={{
                        '& .MuiInputBase-root': {
                          height: 36,
                          lineHeight: 1.4,
                          backgroundColor: isInRetryPaymentPage ? colors.grey200 : 'white',
                          borderColor: colors.grey300,
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: colors.grey300,
                          },
                          '&:hover fieldset': {
                            borderColor: colors.grey300,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.grey300,
                          },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: 14,
                          cursor: isInRetryPaymentPage ? 'not-allowed' : 'auto',
                        },
                      }}
                      minDate={minDate}
                      maxDate={maxDate}
                      shouldDisableDate={date => !filterDate(date)}
                      onChange={(date) => {
                        field.onChange(format(date ?? new Date(), 'yyyy-MM-dd'))
                      }}
                      disabled={isInRetryPaymentPage}
                    />
                  </LocalizationProvider>
                )}
              />
            </VStack>
          )}
          {isShopManager && (
            <VStack gap={2} flex={1}>
              <Text variant="body">收貨時間</Text>
              <Button
                variant="outlined"
                color="grey"
                endIcon={<Clock />}
                rootStyle={[styles.deliveryTime, isInRetryPaymentPage && styles.disabled]}
                xStyles={{
                  endIcon: styles.endIcon,
                }}
                onClick={handleOpen}
                disabled={isInRetryPaymentPage}
              >
                <Text variant="body" color={isInRetryPaymentPage ? colors.grey500 : colors.black}>{DeliveryTimeSlotMap[formDeliveryTimeSlot] || '-'}</Text>
              </Button>
            </VStack>
          )}
        </HStack>
        <Controller
          control={control}
          name="customer_note"
          render={({ field: { ref, ...field } }) => (
            <Input
              {...field}
              rootRef={ref}
              placeholder="【範例：管理室代收、需由本人簽收...等等。】如有產品訂購相關問題，請洽客服協助！"
              multiline
              fullWidth
              minRows={3}
              disabled={isInRetryPaymentPage}
              xStyles={{
                inputBase: {
                  textarea: styles.input,
                },
              }}
            />
          )}
        />
      </CardSection>
      <DialogDeliveryTimeSlot open={isOpen} onClose={handleClose} onSubmit={handleClose} />
    </>
  )
}
